import React, { useState } from 'react';
import { Avatar, Button, Divider, Empty, Input, List, Modal, Select, Space, Table, Tabs, Tag, Typography, message } from 'antd';
import { CommentOutlined, DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';
import FollowUpEditorModal from '../components/FollowUpEditorModal';
import { formatDate, formatDateTime } from '../utils/formatters';
import { brandSemantic } from '../config/brand';

const { Title, Text } = Typography;
const { TextArea } = Input;

const followUpStatusTone = {
  Done: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
  'In Production': { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  Pending: { color: brandSemantic.textSecondary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted },
  Open: { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
  'Not Done': { color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder },
};

const followUpPriorityTone = {
  High: { color: brandSemantic.error, background: brandSemantic.errorSoft, border: brandSemantic.errorBorder },
  Medium: { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
  Low: { color: brandSemantic.success, background: brandSemantic.successSoft, border: brandSemantic.successBorder },
};

const getRoleTeam = (role = '') => {
  if (role === 'admin') return 'admin';
  if (role.includes('procurement')) return 'procurement';
  if (role.includes('sales')) return 'sales';
  return role;
};

const getResponsibleAliases = (user) => {
  if (!user) return [];
  const firstName = user.firstName || user.name?.split(' ')[0] || '';
  const lastName = user.lastName || user.name?.split(' ').slice(1).join(' ') || '';
  const lastInitial = lastName ? lastName[0] : '';
  return [
    user.name,
    `${firstName} ${lastInitial}.`,
    firstName,
  ].filter(Boolean).map((value) => value.toLowerCase());
};

const isResponsibleForUser = (followUp, user) => {
  const responsible = String(followUp.responsible || '').toLowerCase();
  return getResponsibleAliases(user).some((alias) => responsible === alias);
};

const getFollowUpStatusSelectStyle = (status) => {
  const tone = followUpStatusTone[status] || followUpStatusTone.Pending;
  return {
    width: Math.min(Math.max(String(status || '').length * 7 + 34, 108), 160),
    '--status-color': tone.color,
    '--status-bg': tone.background,
    '--status-border': tone.border,
  };
};

const renderToneTag = (value, toneMap, fallbackKey, label = value) => {
  const tone = toneMap[value] || toneMap[fallbackKey];
  return (
    <Tag
      style={{
        color: tone.color,
        background: tone.background,
        borderColor: tone.border,
        fontWeight: 600,
      }}
    >
      {label}
    </Tag>
  );
};

export default function FollowUpsPage({
  followUps,
  setFollowUps,
  offers,
  leads,
  users,
  currentUser,
  openOrderDetails,
  openLeadProfile,
  getFollowUpComments,
  handleAddFollowUpComment,
  canDelete = true,
}) {
  const { t } = useI18n();
  const [mySearchText, setMySearchText] = useState('');
  const [myStatusFilter, setMyStatusFilter] = useState('all');
  const [myPriorityFilter, setMyPriorityFilter] = useState('all');
  const [teamSearchText, setTeamSearchText] = useState('');
  const [teamStatusFilter, setTeamStatusFilter] = useState('all');
  const [teamPriorityFilter, setTeamPriorityFilter] = useState('all');
  const [editingFollowUp, setEditingFollowUp] = useState(null);
  const [viewingFollowUpComments, setViewingFollowUpComments] = useState(null);
  const [newFollowUpComment, setNewFollowUpComment] = useState('');
  const currentUserTeam = getRoleTeam(currentUser?.role);
  const canEditTeamFollowUps = currentUser?.role === 'admin' || currentUser?.role?.includes('manager');

  const enrichedFollowUps = followUps
    .map((followUp) => {
      const offer = offers.find((item) => item.id === followUp.offerId);
      const lead = leads.find((item) => item.id === offer?.clientId);
      const responsibleUser = users.find((user) => isResponsibleForUser(followUp, user));
      return {
        ...followUp,
        offer,
        lead,
        client: offer?.client || '',
        responsibleUser,
        responsibleTeam: getRoleTeam(responsibleUser?.role),
      };
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const myFollowUps = enrichedFollowUps.filter((followUp) => isResponsibleForUser(followUp, currentUser));
  const teamFollowUps = enrichedFollowUps.filter((followUp) => (
    currentUser?.role === 'admin'
      ? true
      : followUp.responsibleTeam && followUp.responsibleTeam === currentUserTeam
  ));

  const filterFollowUps = (items, searchText, statusFilter, priorityFilter) => (
    items.filter((followUp) => {
      if (statusFilter !== 'all' && followUp.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && (followUp.priority || 'Medium') !== priorityFilter) return false;
      if (searchText.trim()) {
        const query = searchText.trim().toLowerCase();
        return (
          String(followUp.action || '').toLowerCase().includes(query)
          || String(followUp.offerId || '').toLowerCase().includes(query)
        );
      }
      return true;
    })
  );

  const filteredMyFollowUps = filterFollowUps(myFollowUps, mySearchText, myStatusFilter, myPriorityFilter);
  const filteredTeamFollowUps = filterFollowUps(teamFollowUps, teamSearchText, teamStatusFilter, teamPriorityFilter);

  const updateFollowUpStatus = (followUpId, status) => {
    setFollowUps((prevFollowUps) => prevFollowUps.map((followUp) => (
      followUp.id === followUpId ? { ...followUp, status } : followUp
    )));
    message.success(t('Follow-up updated'));
  };

  const buildColumns = (canEdit) => [
    {
      title: t('Follow-up date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (value) => <strong>{formatDate(value)}</strong>,
    },
    {
      title: t('Quote / Order'),
      dataIndex: 'offerId',
      key: 'offerId',
      width: 150,
      render: (value, record) => (
        record.offer
          ? (
            <Button type="link" style={{ padding: 0, fontWeight: 600 }} onClick={() => openOrderDetails(record.offer, 'followUps')}>
              {value}
            </Button>
          )
          : value
      ),
    },
    {
      title: t('Client Name'),
      dataIndex: 'client',
      key: 'client',
      width: 190,
      render: (value, record) => (
        record.lead
          ? (
            <Button type="link" style={{ padding: 0 }} onClick={() => openLeadProfile(record.lead, 'followUps')}>
              {value}
            </Button>
          )
          : value || <span style={{ color: '#bfbfbf' }}>—</span>
      ),
    },
    {
      title: t('Description'),
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: t('Responsible'),
      dataIndex: 'responsible',
      key: 'responsible',
      width: 150,
    },
    {
      title: t('Priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 120,
      render: (value = 'Medium') => renderToneTag(value, followUpPriorityTone, 'Medium', t(value)),
    },
    {
      title: t('Status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (value, record) => (
        canEdit
          ? (
            <Select
              value={value}
              onChange={(nextValue) => updateFollowUpStatus(record.id, nextValue)}
              size="small"
              variant="borderless"
              className="status-tag-select"
              style={getFollowUpStatusSelectStyle(value)}
              dropdownMatchSelectWidth={false}
              options={[
                { value: 'Pending', label: t('Pending') },
                { value: 'Open', label: t('Open') },
                { value: 'In Production', label: t('In Production') },
                { value: 'Done', label: t('Done') },
                { value: 'Not Done', label: t('Not Done') },
              ]}
            />
          )
          : renderToneTag(value, followUpStatusTone, 'Pending', t(value))
      ),
    },
    {
      title: t('Comments'),
      key: 'comments',
      width: 110,
      render: (_, record) => {
        const commentItems = getFollowUpComments(record);
        if (commentItems.length === 0) {
          return (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => setViewingFollowUpComments(record)}
            >
              {t('Add')}
            </Button>
          );
        }
        return (
          <Button
            type="link"
            icon={<CommentOutlined />}
            style={{ padding: 0 }}
            onClick={() => setViewingFollowUpComments(record)}
          >
            {commentItems.length}
          </Button>
        );
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 100,
      render: (_, record) => canEdit ? (
        <Space direction="vertical" size={2}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={{ padding: 0, height: 'auto' }}
            onClick={() => setEditingFollowUp(record)}
          >
            {t('Edit')}
          </Button>
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                setFollowUps((prev) => prev.filter((item) => item.id !== record.id));
                if (viewingFollowUpComments?.id === record.id) {
                  setViewingFollowUpComments(null);
                  setNewFollowUpComment('');
                }
                message.success(t('Follow-up deleted'));
              }}
            >
              {t('Delete')}
            </Button>
          )}
        </Space>
      ) : (
        <Tag>{t('Read only')}</Tag>
      ),
    },
  ];

  const renderFilters = ({
    searchText,
    setSearchText,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
  }) => (
    <div className="page-toolbar" style={{ marginBottom: 16 }}>
      <Title level={2} className="page-toolbar-title" style={{ margin: 0 }}>{t('Follow-ups')}</Title>
      <div className="page-toolbar-controls">
      <Space size={12} align="center">
        <Input
          placeholder={t('Search by description or order ID')}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          style={{ width: 180, height: 32, display: 'flex', alignItems: 'center' }}
          suffix={<SearchOutlined />}
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 170 }}
        >
          <Select.Option value="all">{t('All statuses')}</Select.Option>
          <Select.Option value="Pending">{t('Pending')}</Select.Option>
          <Select.Option value="Open">{t('Open')}</Select.Option>
          <Select.Option value="In Production">{t('In Production')}</Select.Option>
          <Select.Option value="Done">{t('Done')}</Select.Option>
          <Select.Option value="Not Done">{t('Not Done')}</Select.Option>
        </Select>
        <Select
          value={priorityFilter}
          onChange={setPriorityFilter}
          style={{ width: 160 }}
        >
          <Select.Option value="all">{t('All priorities')}</Select.Option>
          <Select.Option value="High">{t('High')}</Select.Option>
          <Select.Option value="Medium">{t('Medium')}</Select.Option>
          <Select.Option value="Low">{t('Low')}</Select.Option>
        </Select>
      </Space>
      </div>
    </div>
  );

  return (
    <div>
      <Tabs
        defaultActiveKey="my"
        items={[
          {
            key: 'my',
            label: t('My Follow-ups'),
            children: (
              <>
                {renderFilters({
                  searchText: mySearchText,
                  setSearchText: setMySearchText,
                  statusFilter: myStatusFilter,
                  setStatusFilter: setMyStatusFilter,
                  priorityFilter: myPriorityFilter,
                  setPriorityFilter: setMyPriorityFilter,
                })}
                <Table
                  columns={buildColumns(true)}
                  dataSource={filteredMyFollowUps}
                  rowKey="id"
                  pagination={{ pageSize: 10, position: ['bottomCenter'] }}
                  bordered
                  size="middle"
                  scroll={{ x: 'max-content' }}
                />
              </>
            ),
          },
          {
            key: 'team',
            label: t('My Team Follow-ups'),
            children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {renderFilters({
                  searchText: teamSearchText,
                  setSearchText: setTeamSearchText,
                  statusFilter: teamStatusFilter,
                  setStatusFilter: setTeamStatusFilter,
                  priorityFilter: teamPriorityFilter,
                  setPriorityFilter: setTeamPriorityFilter,
                })}
                <Table
                  columns={buildColumns(canEditTeamFollowUps)}
                  dataSource={filteredTeamFollowUps}
                  rowKey="id"
                  pagination={{ pageSize: 10, position: ['bottomCenter'] }}
                  bordered
                  size="middle"
                  scroll={{ x: 'max-content' }}
                />
              </Space>
            ),
          },
        ]}
      />
      <FollowUpEditorModal
        open={Boolean(editingFollowUp)}
        followUp={editingFollowUp}
        users={users}
        currentUser={currentUser}
        t={t}
        onCancel={() => setEditingFollowUp(null)}
        onSave={(values) => {
          setFollowUps((prev) => prev.map((item) => (
            item.id === values.id ? { ...item, ...values } : item
          )));
          setEditingFollowUp(null);
          message.success(t('Follow-up updated'));
        }}
      />
      <Modal
        title={t('Comments')}
        open={Boolean(viewingFollowUpComments)}
        onCancel={() => {
          setViewingFollowUpComments(null);
          setNewFollowUpComment('');
        }}
        footer={null}
        width={760}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <TextArea
            rows={3}
            placeholder={t('Add a new comment...')}
            value={newFollowUpComment}
            onChange={(event) => setNewFollowUpComment(event.target.value)}
            style={{ marginBottom: 8 }}
          />
          <Button
            type="primary"
            onClick={() => {
              const createdComment = handleAddFollowUpComment(viewingFollowUpComments?.id, newFollowUpComment);
              if (createdComment) {
                setViewingFollowUpComments((prev) => ({
                  ...prev,
                  comments: [...getFollowUpComments(prev), createdComment],
                }));
              }
              setNewFollowUpComment('');
            }}
          >
            {t('Add Comment')}
          </Button>
        </div>
        <Divider />
        {viewingFollowUpComments && getFollowUpComments(viewingFollowUpComments).length === 0 ? (
          <Empty description={t('No comments yet')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            dataSource={viewingFollowUpComments ? [...getFollowUpComments(viewingFollowUpComments)].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) : []}
            renderItem={(comment) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={(
                    <Space>
                      <strong>{comment.author}</strong>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatDateTime(comment.timestamp)}
                      </Text>
                    </Space>
                  )}
                  description={comment.comment}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </div>
  );
}
